import {
  ApplicationView,
  ControlledComponent,
  useController,
} from '@aha-app/mvc';
import React, { useState } from 'react';
import VotingSessionController from './controllers/VotingSessionController';
import styles from './VotingSession.module.css';
import classNames from 'classnames';
import { type WhiteboardSidePanelItem } from '../../../sidepanel-types';
import ResultsPanel from './ResultsPanel';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import DeleteConfirmation from './DeleteConfirmation';
import { formattedTimestamp } from '../../../utils/time';
import { VotingSessionTypeData } from './VotingSetupSessionTypeControls';

function resultItem(controller, widget): WhiteboardSidePanelItem {
  return {
    id: 'votingResults',
    name: widget.name,
    icon: 'aha-icon aha-icon-voting',
    panel: (
      <ControlledComponent controller={controller}>
        <ResultsPanel widget={widget} />
      </ControlledComponent>
    ),
  };
}

const ResultsList: React.FC = () => {
  const controller = useController(VotingSessionController);

  const { widgets } = controller;
  const [deletingSession, setDeletingSession] =
    useState<VotingSessionWidget | null>(null);

  if (deletingSession) {
    return (
      <DeleteConfirmation
        widget={deletingSession}
        onConfirm={() => {
          controller.actionRemoveVotingSession(deletingSession);
          setDeletingSession(null);
        }}
        onCancel={() => setDeletingSession(null)}
      />
    );
  }

  return (
    <div>
      <ol className='side-panel__list'>
        {[...widgets].reverse().map(widget => (
          <li key={widget.id}>
            <div
              className={classNames(
                'flex-row',
                'flex-row--align-center',
                'flex-row--justify-space-between',
                'side-panel__hover-background',
                styles.votingResult,
                {
                  [styles.votingResultDisabled]: widget.isActive,
                }
              )}
            >
              <div
                className={classNames(
                  'pt-4 pb-4 pl-4',
                  styles.votingGrowAction,
                  {
                    [styles.votingResultAction]: !widget.isActive,
                  }
                )}
                onClick={() =>
                  !widget.isActive &&
                  controller.actionPushSidePanel(resultItem(controller, widget))
                }
              >
                <div className={classNames('h-600', styles.votingResultName)}>
                  <div>
                    {VotingSessionTypeData[widget.sessionType].iconLarge}
                  </div>
                  <div>{widget.name}</div>
                </div>
                <span
                  className={classNames(
                    'text-muted',
                    'mr-2',
                    styles.votingResultDate
                  )}
                >
                  {formattedTimestamp(widget.startedAt, { time: false })}
                </span>
              </div>
              <div
                className={classNames(
                  'pt-4 pb-4 pr-4',
                  styles.votingResultActions
                )}
              >
                {widget.isActive && (
                  <aha-pill
                    color='var(--theme-alert-warning-background)'
                    text-color='var(--theme-alert-warning-text)'
                    border-color='var(--theme-alert-warning-border)'
                    border-shape='round'
                  >
                    Active session
                  </aha-pill>
                )}
                <aha-button
                  class={classNames(styles.votingResultDeleteButton, 'py-0')}
                  kind='plain'
                  onClick={() => {
                    setDeletingSession(widget);
                  }}
                >
                  <aha-icon icon='fa-regular fa-trash' />
                </aha-button>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default ApplicationView(ResultsList);
